import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const searchMembersForRegister = (q) => api.get('/auth/members-public', { params: { q } });
export const getMe = () => api.get('/auth/me');
export const changePassword = (data) => api.put('/auth/change-password', data);
export const verifyPassword = (data) => api.post('/auth/verify-password', data);

// Members
export const getMembers = (params) => api.get('/members', { params });
export const getMemberStats = () => api.get('/members/stats');

// Signatories (v1.9.2) — upload a signature image for one of the three slots
// (prepared|received|approved). File field name is `signature`.
export const uploadSignatorySignature = (slot, file) => {
  const fd = new FormData();
  fd.append('signature', file);
  return api.post(`/settings/signatory/${slot}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteSignatorySignature = (slot) => api.delete(`/settings/signatory/${slot}`);
export const getMemberDemographics = (category) => api.get('/members/demographics', { params: { category } });
export const getMember = (id) => api.get(`/members/${id}`);
export const createMember = (data) => api.post('/members', data);
export const importMembers = (rows) => api.post('/members/import', { rows });
export const updateMember = (id, data) => api.put(`/members/${id}`, data);
export const deleteMember = (id) => api.delete(`/members/${id}`);
export const toggleLeader = (id) => api.put(`/members/${id}/toggle-leader`);
// v1.6.1 — per-member 'pays for themselves' flag for sub-members
export const setPaysIndependently = (id, value) => api.put(`/members/${id}/pays-independently`, { pays_independently: value ? 1 : 0 });
export const getMemberDetail = (id) => api.get(`/members/${id}`);
export const addDependent = (id, data) => api.post(`/members/${id}/dependents`, data);
export const deleteDependent = (depId) => api.delete(`/members/dependents/${depId}`);
export const updateDependentLifeStatus = (depId, data) => api.put(`/members/dependents/${depId}/life-status`, data);
export const getDependentStatusHistory = (depId) => api.get(`/members/dependents/${depId}/status-history`);
export const addMemberFollowup = (id, data) => api.post(`/members/${id}/followups`, data);
export const changeMemberStatus = (id, data) => api.post(`/members/${id}/status`, data);
export const deleteMemberFollowup = (fupId) => api.delete(`/members/followups/${fupId}`);
export const getTezkar = (id) => api.get(`/members/${id}/tezkar`);
export const addTezkar = (id, data) => api.post(`/members/${id}/tezkar`, data);
export const deleteTezkar = (tezkarId) => api.delete(`/members/tezkar/${tezkarId}`);
export const reorderTezkar = (memberId, ids) => api.put(`/members/${memberId}/tezkar/reorder`, { ids });
export const getFatherMembers = (id) => api.get(`/spiritual/fathers/${id}/members`);
export const getMyFollowupQrToken = () => api.get('/spiritual/qr-token');
export const previewFollowupScan = (token) => api.post('/spiritual/followups/preview', { token });
export const saveFollowupScan = (token, notes) => api.post('/spiritual/followups/scan', { token, notes });
export const getMyFollowupBadges = () => api.get('/spiritual/my-followup-badges');

// Attendance
export const getServices = (params) => api.get('/attendance/services', { params });
export const createService = (data) => api.post('/attendance/services', data);
export const deleteService = (id) => api.delete(`/attendance/services/${id}`);
export const updateServiceCount = (id, data) => api.put(`/attendance/services/${id}/count`, data);
export const markAttendance = (id, data) => api.post(`/attendance/services/${id}/mark`, data);
export const getAttendanceRecords = (id) => api.get(`/attendance/services/${id}/records`);
export const getServiceQrToken = (id) => api.get(`/attendance/services/${id}/qr-token`);
export const scanAttendance = (token) => api.post('/attendance/scan-attend', { token });
export const getAbsentees = (params) => api.get('/attendance/absentees', { params });
export const getAttendanceHistory = (params) => api.get('/attendance/history', { params });

// Spiritual
export const getSpiritualFathers = () => api.get('/spiritual/fathers');
export const getEligibleFathers = () => api.get('/spiritual/fathers/eligible');
export const getSpiritualFamilies = (params) => api.get('/spiritual/families', { params });
export const getSpiritualFamily = (id) => api.get(`/spiritual/families/${id}`);
export const createSpiritualFamily = (data) => api.post('/spiritual/families', data);
export const updateSpiritualFamily = (id, data) => api.put(`/spiritual/families/${id}`, data);
export const deleteSpiritualFamily = (id) => api.delete(`/spiritual/families/${id}`);
export const reassignSpiritualFamily = (id, data) => api.put(`/spiritual/families/${id}/reassign`, data);
export const bulkAssignToFather = (data) => api.post('/spiritual/bulk-assign', data);
export const addFamilyMember = (familyId, data) => api.post(`/spiritual/families/${familyId}/members`, data);
export const updateFamilyMember = (id, data) => api.put(`/spiritual/family-members/${id}`, data);
export const deleteFamilyMember = (id) => api.delete(`/spiritual/family-members/${id}`);
export const createFollowup = (data) => api.post('/spiritual/followups', data);
export const getFollowups = (params) => api.get('/spiritual/followups', { params });
export const getFollowupSummary = () => api.get('/spiritual/followup-summary');
export const deleteFollowup = (id) => api.delete(`/spiritual/followups/${id}`);

// Finance
export const getCR = (params) => api.get('/finance/cr', { params });
export const getCRByNumber = (crNumber) => api.get(`/finance/cr/by-number/${encodeURIComponent(crNumber)}`);
export const createCR = (data) => api.post('/finance/cr', data);
export const updateCRStatus = (id, data) => api.put(`/finance/cr/${id}/status`, data);
export const deleteCR = (id, reason) => api.delete(`/finance/cr/${id}`, { data: { reason } });
export const getCRDeletionRequests = (params) => api.get('/finance/cr/deletion-requests', { params });
export const decideCRDeletion = (id, decision, reject_note) => api.put(`/finance/cr/deletion-requests/${id}`, { decision, reject_note });
export const getPV = (params) => api.get('/finance/pv', { params });
export const createPV = (data) => api.post('/finance/pv', data);
export const updatePVStatus = (id, data) => api.put(`/finance/pv/${id}/status`, data);
export const getPVPendingCounts = () => api.get('/finance/pv/pending-counts');
// In-app notification inbox
export const getNotifications = () => api.get('/notifications');
export const getNotificationUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');
// Information hub — free-form sections (admin-editable, member-viewable)
export const listInfoSections = () => api.get('/info');
export const getInfoPage = (slug) => api.get(`/info/${slug}`);
export const createInfoSection = (data) => api.post('/info', data);
export const saveInfoPage = (slug, data) => api.put(`/info/${slug}`, data);
export const deleteInfoSection = (slug) => api.delete(`/info/${slug}`);
export const reorderInfoSections = (order) => api.put('/info/reorder', { order });
export const uploadInfoImage = (slug, formData) => api.post(`/info/${slug}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteInfoImage = (id) => api.delete(`/info/images/${id}`);
// Member self-service contact update (phone + whatsapp)
export const updateMyContact = (data) => api.put('/members/me/contact', data);
// v1.5.2 — expanded self-service edit. Backend only accepts 11 whitelisted
// fields; anything else in `data` is silently dropped server-side.
export const updateMyProfile = (data) => api.put('/members/me/profile', data);
export const setMyWhatsappOptIn = (opt_in) => api.put('/members/me/whatsapp-opt-in', { opt_in: opt_in ? 1 : 0 });
export const setMemberWhatsappOptIn = (memberId, opt_in) => api.put(`/members/${memberId}/whatsapp-opt-in`, { opt_in: opt_in ? 1 : 0 });

// v1.6.0 — Outstanding-debt ledger
export const getMemberOutstanding = (memberId, all = false) => api.get(`/members/${memberId}/outstanding`, { params: all ? { all: 1 } : undefined });
export const addMemberOutstanding = (memberId, data) => api.post(`/members/${memberId}/outstanding`, data);
export const deleteOutstandingEntry = (entryId) => api.delete(`/members/outstanding/${entryId}`);
export const settleOutstandingManual = (memberId, data) => api.post(`/members/${memberId}/outstanding/settle`, data);
export const getMyOutstanding = () => api.get('/members/me/outstanding');
export const getOutstandingSummary = () => api.get('/members/outstanding/summary');
// Autocomplete for the "Ordained By" field on clergy members
export const getOrdainedAuthorities = () => api.get('/members/ordained-authorities');
// Autocomplete for the "Nationality" field on any member
export const getNationalities = () => api.get('/members/nationalities');
// Promote a dependent to a sub-member (card MCN-XXXX/N, linked via head_member_id)
export const promoteDependent = (memberId, depId) => api.post(`/members/${memberId}/dependents/${depId}/promote`);
export const deletePV = (id) => api.delete(`/finance/pv/${id}`);
export const getAP = (params) => api.get('/finance/ap', { params });
export const createAP = (data) => api.post('/finance/ap', data);
export const payAP = (id, data) => api.put(`/finance/ap/${id}/pay`, data);
export const deleteAP = (id) => api.delete(`/finance/ap/${id}`);
export const getCashBook = (params) => api.get('/finance/cashbook', { params });
export const getCashTransfers = (params) => api.get('/finance/cash-transfers', { params });
// v1.9.62 — accepts an optional File in data.attachment. Sent as multipart
// so the multer middleware can save it under uploads/ prefixed 'trf-'.
export const createCashTransfer = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    fd.append(k, v);
  });
  return api.post('/finance/cash-transfers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteCashTransfer = (id) => api.delete(`/finance/cash-transfers/${id}`);
// v1.9.105 — Currency Exchanges (cross-currency bucket moves). Same multipart
// upload pattern as transfers for the optional receipt attachment.
export const getCurrencyExchanges = (params) => api.get('/finance/currency-exchanges', { params });
export const createCurrencyExchange = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    fd.append(k, v);
  });
  return api.post('/finance/currency-exchanges', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteCurrencyExchange = (id) => api.delete(`/finance/currency-exchanges/${id}`);
export const getOpeningBalance        = (history = false) => api.get('/finance/cashbook/opening', { params: history ? { history: 1 } : undefined });
export const saveOpeningBalance       = (data) => api.post('/finance/cashbook/opening', data);
export const deleteOpeningBalanceRow  = (id) => api.delete(`/finance/cashbook/opening/${id}`);
export const getFundCategories = ({ all = false } = {}) => api.get('/finance/fund-categories', { params: all ? { all: 1 } : undefined });
export const createFundCategory = (data) => api.post('/finance/fund-categories', data);
export const updateFundCategory = (id, data) => api.put(`/finance/fund-categories/${id}`, data);
export const deleteFundCategory = (id) => api.delete(`/finance/fund-categories/${id}`);
export const getExpenseCategories = () => api.get('/finance/expense-categories');
export const createExpenseCategory = (data) => api.post('/finance/expense-categories', data);
export const updateExpenseCategory = (id, data) => api.put(`/finance/expense-categories/${id}`, data);
export const deleteExpenseCategory = (id) => api.delete(`/finance/expense-categories/${id}`);

// Stock
export const getItems = () => api.get('/stock/items');
export const createItem = (data) => api.post('/stock/items', data);
export const updateItem = (id, data) => api.put(`/stock/items/${id}`, data);
export const deleteItem = (id) => api.delete(`/stock/items/${id}`);
export const getGRNList = (params) => api.get('/stock/grn', { params });
export const getGRN = (id) => api.get(`/stock/grn/${id}`);
export const createGRN = (data) => api.post('/stock/grn', data);
export const deleteGRN = (id) => api.delete(`/stock/grn/${id}`);
export const getSIVList = (params) => api.get('/stock/siv', { params });
export const getSIV = (id) => api.get(`/stock/siv/${id}`);
export const createSIV = (data) => api.post('/stock/siv', data);
export const deleteSIV = (id) => api.delete(`/stock/siv/${id}`);
// Fixed assets register
export const getAssets = (params) => api.get('/stock/assets', { params });
export const getAsset = (id) => api.get(`/stock/assets/${id}`);
export const createAsset = (data) => api.post('/stock/assets', data);
export const updateAsset = (id, data) => api.put(`/stock/assets/${id}`, data);
export const assignAsset = (id, data) => api.post(`/stock/assets/${id}/assign`, data);
export const returnAsset = (id, data) => api.post(`/stock/assets/${id}/return`, data);
export const disposeAsset = (id, data) => api.post(`/stock/assets/${id}/dispose`, data);
export const deleteAsset = (id) => api.delete(`/stock/assets/${id}`);

// Reset data (admin)
export const resetData = (data) => api.post('/settings/reset-data', data);

// Subscription Status Report (admin) and reminders
export const getSubscriptionStatus = (params) => api.get('/contributions/status-report', { params });
export const sendSubscriptionReminder = (data) => api.post('/contributions/remind', data);
export const getMyReminders = () => api.get('/contributions/my-reminders');
export const sendDueRemindersNow = () => api.post('/contributions/run-due-reminders');
export const dismissMyReminder = (id) => api.post(`/contributions/my-reminders/${id}/dismiss`);

// Reports
export const getMembershipReport = () => api.get('/reports/membership');
export const getSpiritualFatherReport = (params) => api.get('/reports/spiritual-father', { params });
export const getAssignmentReport = () => api.get('/reports/assignment');
export const getAttendanceReport = (params) => api.get('/reports/attendance', { params });
export const getServiceAttendance = (id) => api.get(`/reports/attendance/service/${id}`);
export const getFinancialReport = (params) => api.get('/reports/financial', { params });
export const getCRByNationality = (params) => api.get('/reports/cr-by-nationality', { params });
export const getExpectedByNationality = (params) => api.get('/reports/expected-by-nationality', { params });
export const getGivingReport = (params) => api.get('/reports/giving', { params });

// Settings
export const getChurchProfile = () => api.get('/settings/profile');
export const getLiveFxRate = () => api.get('/settings/fx-rate/live');
export const updateChurchProfile = (data) => api.put('/settings/profile', data);
export const getUsers = () => api.get('/settings/users');
export const approveUser = (id, data) => api.put(`/settings/users/${id}/approve`, data);
export const createUser = (data) => api.post('/settings/users', data);
export const updateUser = (id, data) => api.put(`/settings/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/settings/users/${id}`);
export const getDepartments = (params) => api.get('/settings/departments', { params });
export const createDepartment = (data) => api.post('/settings/departments', data);
export const updateDepartment = (id, data) => api.put(`/settings/departments/${id}`, data);
export const deleteDepartment = (id) => api.delete(`/settings/departments/${id}`);
export const initiateLencoForMember = (data) => api.post('/lenco/initiate-for-member', data);
export const getLencoLocalStatus = (reference) => api.get(`/lenco/local-status/${reference}`);
export const getPermissions = () => api.get('/settings/permissions');
export const updateRolePermissions = (role, permission_keys) => api.put('/settings/permissions', { role, permission_keys });

// Contribution Subscriptions
export const subscribeToType = (data) => api.post('/contributions/subscribe', data);
export const updatePledge = (typeId, data) => api.put(`/contributions/subscribe/${typeId}`, data);
// v1.6.3 — admin-only. member_id is required so admin can unsubscribe any
// member from their profile page; portal-side callers are gone.
export const unsubscribeFromType = (typeId, memberId) => api.delete(`/contributions/subscribe/${typeId}`, { params: memberId ? { member_id: memberId } : undefined });
export const getMySubscriptions = () => api.get('/contributions/my');
// v1.6.6 — portal-wide status banner (overdue / due-soon / up-to-date)
export const getMyContributionSummary = () => api.get('/contributions/my-summary');

// v1.7.0 — Lenco Pay integration
export const getLencoConfig    = () => api.get('/lenco/config');
export const initiateLenco     = (data) => api.post('/lenco/initiate', data);
export const verifyLenco       = (reference) => api.get(`/lenco/verify/${encodeURIComponent(reference)}`);
// v1.9.4 — cheap own-DB status poll (no Lenco round-trip).
export const getLocalLencoStatus = (reference) => api.get(`/lenco/local-status/${encodeURIComponent(reference)}`);
export const getOverdueSubscriptions = () => api.get('/contributions/overdue');
export const getTypeStats = (id) => api.get(`/contributions/types/${id}/stats`);
export const markPeriodPaid = (id, data) => api.put(`/contributions/periods/${id}/pay`, data);

// Payment Requests
export const submitPaymentRequest = (formData) => api.post('/payments/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getMyPayments = () => api.get('/payments/my');
export const getPendingPayments = () => api.get('/payments/pending');
export const getAllPayments = (params) => api.get('/payments/all', { params });
export const approvePayment = (id) => api.put(`/payments/${id}/approve`);
export const rejectPayment = (id, data) => api.put(`/payments/${id}/reject`, data);

export default api;
